import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, ImageIcon } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CtaBand } from "../components/CtaBand";
import { API_URL } from "../config";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ProjectPhoto = ({ url, alt, label }) => (
  <div className="relative aspect-[4/3] bg-[#FAFAFA] overflow-hidden" data-testid={`project-photo-${label.toLowerCase()}`}>
    {url ? (
      <img src={`${BACKEND}${url}`} alt={alt} className="w-full h-full object-cover" loading="lazy" />
    ) : (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#A3A3A3]">
        <ImageIcon className="w-8 h-8" />
        <span className="text-xs font-medium">{label} photo coming soon</span>
      </div>
    )}
    <span className="absolute top-3 left-3 text-xs font-semibold bg-white/90 backdrop-blur rounded-full px-3 py-1 text-[#4A3018]">
      {label}
    </span>
  </div>
);

const ProjectImages = ({ project }) => {
  const { suburb, before_url, after_url, photos = [] } = project;
  if (before_url) {
    return (
      <div className="grid grid-cols-2">
        <ProjectPhoto url={before_url} alt={`Before flooring installation in ${suburb}`} label="Before" />
        <ProjectPhoto url={after_url} alt={`After hybrid flooring installation in ${suburb}`} label="After" />
      </div>
    );
  }
  const shots = [after_url, ...photos].filter(Boolean);
  if (shots.length === 0) {
    return <ProjectPhoto url={null} alt="" label="After" />;
  }
  return (
    <div className={shots.length > 1 ? "grid grid-cols-2" : ""}>
      {shots.map((url, i) => (
        <ProjectPhoto key={url} url={url} alt={`Completed hybrid flooring installation in ${suburb} — photo ${i + 1}`} label="After" />
      ))}
    </div>
  );
};

export default function Projects() {
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/projects`).then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  return (
    <div data-testid="projects-page">
      <PageHeader
        label="Recent Projects"
        title="Before and after hybrid flooring installations"
        description="A look at recent residential flooring work across Melbourne. Real project photos are added as jobs are completed — no stock photos, no made-up numbers."
        testId="projects-header"
      />

      <section className="py-20 md:py-24" data-testid="projects-gallery">
        <div className="max-w-7xl mx-auto px-6">
          {projects === null ? (
            <p className="text-[#525252]" data-testid="projects-loading">Loading projects…</p>
          ) : projects.length === 0 ? (
            <div className="border border-dashed border-[#E5E5E5] rounded-2xl p-12 text-center max-w-2xl mx-auto" data-testid="projects-empty">
              <ImageIcon className="w-10 h-10 text-[#C87A38] mx-auto" />
              <h2 className="mt-4 text-xl font-semibold font-heading">Project photos coming soon</h2>
              <p className="mt-2 text-sm text-[#525252] leading-relaxed">
                We are currently uploading before and after photos from recent installations around Melbourne.
                Check back shortly, or request a quote and ask to see examples of our work.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {projects.map((p, i) => (
                <article key={p.id} className="border border-[#E5E5E5] rounded-xl overflow-hidden bg-white fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`project-card-${i}`}>
                  <ProjectImages project={p} />
                  <div className="p-6">
                    <h2 className="text-lg font-semibold font-heading flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#C87A38]" /> {p.suburb}
                    </h2>
                    <dl className="mt-3 space-y-1.5 text-sm text-[#525252]">
                      {p.colour && <div className="flex gap-2"><dt className="font-semibold text-[#4A3018] w-28 shrink-0">Flooring colour</dt><dd>{p.colour}</dd></div>}
                      {p.area && <div className="flex gap-2"><dt className="font-semibold text-[#4A3018] w-28 shrink-0">Floor area</dt><dd>{p.area}</dd></div>}
                      {p.services && <div className="flex gap-2"><dt className="font-semibold text-[#4A3018] w-28 shrink-0">Services</dt><dd>{p.services}</dd></div>}
                    </dl>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <CtaBand title="Want results like these in your home?" text="Send us your measurements and a few photos of your current floors for a clear, written quote." />
    </div>
  );
}
